import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  fetchServicesSectionAdmin,
  saveServicesSection,
  uploadServiceMedia,
  deleteServiceMedia,
} from "./api";
import { emptyBlock } from "./utils";

export function useServicesSection(enabled = true) {
  const [section, setSection] = useState(null); // { titulo, visible, bloques }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBlockId, setUploadingBlockId] = useState(null);
  // Última versión confirmada en el servidor: sirve para saber, al Guardar, qué
  // archivos de Cloudinary quedaron huérfanos (quitados localmente pero aún sin persistir).
  const originalSectionRef = useRef(null);

  const fetchSection = useCallback(async () => {
    try {
      const res = await fetchServicesSectionAdmin();
      setSection(res.data);
      originalSectionRef.current = res.data;
    } catch (e) {
      toast.error("Error al cargar la sección de Servicios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) fetchSection(); }, [enabled, fetchSection]);

  const updateSectionField = (field, value) => setSection((s) => ({ ...s, [field]: value }));

  const updateBlock = (blockId, field, value) => {
    setSection((s) => ({
      ...s,
      bloques: s.bloques.map((b) => (b.id === blockId ? { ...b, [field]: value } : b)),
    }));
  };

  const addBlock = () => setSection((s) => ({ ...s, bloques: [...s.bloques, emptyBlock()] }));
  const removeBlock = (blockId) => {
    if (!window.confirm("¿Eliminar este bloque de servicio por completo? Esta acción no se puede deshacer.")) return;
    setSection((s) => ({ ...s, bloques: s.bloques.filter((b) => b.id !== blockId) }));
  };

  const handleUpload = async (blockId, file) => {
    setUploadingBlockId(blockId);
    try {
      const res = await uploadServiceMedia(file);
      setSection((s) => ({
        ...s,
        bloques: s.bloques.map((b) => (b.id === blockId ? { ...b, media: [...b.media, res.data] } : b)),
      }));
      toast.success("Archivo subido. No olvides Guardar para publicarlo.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir el archivo");
    } finally {
      setUploadingBlockId(null);
    }
  };

  // Quita la foto/video solo del estado local; el archivo real en Cloudinary se
  // borra hasta hacer clic en Guardar (así, si el admin no guarda, no se pierde nada).
  const removeMedia = (blockId, media) => {
    if (!window.confirm("¿Eliminar esta foto/video? Se quitará de Cloudinary al Guardar.")) return;
    setSection((s) => ({
      ...s,
      bloques: s.bloques.map((b) => (b.id === blockId ? { ...b, media: b.media.filter((m) => m.id !== media.id) } : b)),
    }));
  };

  const reorderMedia = (blockId, fromIndex, toIndex) => {
    setSection((s) => ({
      ...s,
      bloques: s.bloques.map((b) => {
        if (b.id !== blockId) return b;
        const media = [...b.media];
        const [moved] = media.splice(fromIndex, 1);
        media.splice(toIndex, 0, moved);
        return { ...b, media };
      }),
    }));
  };

  const updateMediaCaption = (blockId, mediaId, caption) => {
    setSection((s) => ({
      ...s,
      bloques: s.bloques.map((b) => (b.id !== blockId ? b : {
        ...b,
        media: b.media.map((m) => (m.id === mediaId ? { ...m, caption } : m)),
      })),
    }));
  };

  /** Archivos que existían en la última versión guardada y ya no están en `section` (quitados o bloque eliminado). */
  const findOrphanedCloudinaryMedia = () => {
    const original = originalSectionRef.current;
    if (!original) return [];
    const currentIds = new Set(section.bloques.flatMap((b) => b.media.map((m) => m.id)));
    return original.bloques
      .flatMap((b) => b.media)
      .filter((m) => m.cloudinary_public_id && !currentIds.has(m.id));
  };

  const save = async () => {
    setSaving(true);
    try {
      const orphans = findOrphanedCloudinaryMedia();
      await Promise.all(orphans.map((m) =>
        deleteServiceMedia(m.cloudinary_public_id, m.tipo === "video" ? "video" : "image").catch(() => {})
      ));
      await saveServicesSection(section);
      originalSectionRef.current = section;
      toast.success("Sección de Servicios guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar la sección de Servicios");
    } finally {
      setSaving(false);
    }
  };

  return {
    section, loading, saving, uploadingBlockId,
    updateSectionField, updateBlock, addBlock, removeBlock,
    handleUpload, removeMedia, reorderMedia, updateMediaCaption,
    save,
  };
}
